import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_FIREWALL_SETTINGS } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  FirewallExclusionItem,
  FirewallIdsProfileType,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  FirewallSettingsFormValue,
  FirewallSettingsSearchableKeywords,
} from '../../../../utils/uiSettings/firewall/firewall.types';
import {
  FIREWALL_SETTINGS_DEFAULT_VALUE,
  getFirewallSettingsSearchableKeywords,
  getLogVerbosityLevelOptions,
} from '../../../../utils/uiSettings/firewall/firewall.values';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { GzFirewallSettingsExclusionsTableWrapperWebComponent } from '../gridComponents/GzFirewallSettingsExclusionsTableWebComponent';
import { GzSelectionCardGroupWebComponent } from '../../../../../../../ui/gzSelectionCardGroup';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';

@customElement(WEB_COMPONENT_POLICY_PANEL_FIREWALL_SETTINGS)
export class GzFirewallSettingsWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  FirewallSettingsFormValue,
  FirewallSettingsSearchableKeywords
> {
  /**
   * Path for Firewall - General section.
   */
  protected path = {
    moduleId: ModuleId.FIREWALL,
    sectionId: SectionId.SETTINGS,
  };

  /**
   * Searchable keywords for Firewall - General section.
   */
  protected searchableKeywords = getFirewallSettingsSearchableKeywords();

  @query('#firewall-settings-toggle')
  private firewallToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#firewall-settings-info-text')
  private firewallInfoText: GzTextWebComponent;
  @query('#firewall-settings-content')
  private firewallContent: HTMLElement;
  @query('#firewall-settings-info-text')
  private firewallSettingsInfoText: HTMLElement;
  @query('#blocklist-rules-network-connection-warning-text')
  private blocklistRulesWarningText: HTMLElement;

  // selectors
  @query('#ics-form-field')
  private icsFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#monitor-form-field')
  private monitorFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#log-checkbox-form-field')
  private logCheckboxFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#log-input-form-field')
  private logInputFormField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#block-port-scan-form-field')
  private blockPortScanFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#exclusions-items-form-field')
  private exclusionsItemsFormField: GzFormFieldWebComponent<
    GzFirewallSettingsExclusionsTableWrapperWebComponent,
    FirewallExclusionItem[]
  >;
  @query('#firewall-exclusions-content-text')
  private exclusionsContentText: GzTextWebComponent;
  @query('#firewall-exclusions-checkbox-form-field')
  private exclusionsCheckboxFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#ids-toggle')
  private idsToggleFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#ids-content')
  private idsContent: HTMLElement;
  @query('#ids-profile-form-field')
  private idsProfileFormField: GzFormFieldWebComponent<GzSelectionCardGroupWebComponent>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="firewall-settings-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Firewall].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <span>${this.searchableKeywords[FirewallSettingsSearchableKeywords.Firewall].translatedLabel}</span>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="firewall-settings-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn('Firewall protects endpoints from unauthorized inbound and outbound connection attempts.')}
    </gz-text>
    <gz-info-box
      id="blocklist-rules-network-connection-warning-text"
      class="padding-top-after-info-text hidden pl-0"
      severity="warning"
    >
      ${translateFn(
        'Disabling the Firewall module, regardless of the method, will also disable network connection blocklist rules in the Blocklist section.'
      )}
    </gz-info-box>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="firewall-settings-content">
        <gz-form-field id="ics-form-field" class="mt-4">
          <gz-checkbox
            searchable-label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Ics].label}"
            gz-form-input
            label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Ics].translatedLabel}"
          ></gz-checkbox>
        </gz-form-field>

        <gz-form-field id="monitor-form-field" class="mt-2">
          <gz-checkbox
            searchable-label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.MonitorWifi].label}"
            gz-form-input
            label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.MonitorWifi].translatedLabel}"
          ></gz-checkbox>
        </gz-form-field>

        <div class="checkbox-with-input">
          <gz-form-field class="label-mw-250 checkbox-width" id="log-checkbox-form-field">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Log].label}"
              label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Log].translatedLabel + ':'}"
              gz-form-input
              @change="${e => toggleDisabled(this.logInputFormField, !e.detail.checked)}"
            ></gz-checkbox>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="log-input-form-field">
            <gz-dropdown gz-form-input .options="${getLogVerbosityLevelOptions()}"></gz-dropdown>
          </gz-form-field>
        </div>

        <gz-form-field id="block-port-scan-form-field">
          <gz-checkbox
            gz-form-input
            searchable-label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.BlockScan].label}"
            label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.BlockScan].translatedLabel}"
            @change="${this.toggleDisabledBlockPortScan}"
          ></gz-checkbox>
        </gz-form-field>

        <div class="firewall-exclusions helper-text">
          <gz-form-field id="firewall-exclusions-checkbox-form-field" class="mbw-1">
            <gz-checkbox
              gz-form-input
              searchable-label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Exclusions].label}"
              label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Exclusions].translatedLabel}"
              @change="${this.toggleDisabledBlockPortScan}"
            ></gz-checkbox>
          </gz-form-field>

          <gz-text id="firewall-exclusions-content-text" fontSize="12" fontWeight="400" class="infoText"
            >${translateFn('This option is compatible with Bitdefender Endpoint Security Tools for Windows.')}
          </gz-text>

          <gz-form-field id="exclusions-items-form-field">
            <gz-policy-panel-firewall-settings-exclusions-table
              gz-form-input
            ></gz-policy-panel-firewall-settings-exclusions-table>
          </gz-form-field>
        </div>
      </div>

      <div class="mt-4">
        <gz-form-field id="ids-toggle" status-field class="mbw-1">
          <gz-toggle
            class="content-title-toggle"
            searchable-label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Ids].label}"
            gz-form-input
            @toggleChange="${e => toggleDisabled(this.idsContent, !e.detail.checked)}"
          >
            <span>${this.searchableKeywords[FirewallSettingsSearchableKeywords.Ids].translatedLabel}</span>
          </gz-toggle>
        </gz-form-field>

        <div id="ids-content">
          <gz-text id="ids-description-text" fontSize="12" fontWeight="400" class="infoText"
            >${translateFn(
              'Intrusion Detection System (IDS) policy settings only apply to Endpoint Security (legacy security agent).'
            )} <br />${translateFn(
              'Bitdefender Endpoint Security Tools agent integrates Host-Based Intrusion Detection System capabilities in its Advanced Threat Control (ATC) module.'
            )}
          </gz-text>

          <gz-form-field id="ids-profile-form-field" class="mt-2">
            <gz-selection-card-group gz-form-input>
              <gz-selection-card
                searchable-label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Aggressive].label}"
                label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Aggressive].translatedLabel}"
                .value=${FirewallIdsProfileType.AGGRESSIVE}
                id="ids-profile-aggressive"
              >
                <div slot="content">
                  <gz-title fontSize="12" fontWeight="600" style="margin-bottom: 8px"
                    >${translateFn('Recommended for critical systems')}</gz-title
                  >
                  <gz-text
                    fontSize="12"
                    style="margin-bottom: 8px"
                    .innerHTML="${translateFn(
                      'Flags keylogging attempts, Internet Explorer leaks and attempts to change desktop background or the IE home page.{break}Protects against DLL injections, installation of malware drivers.{break}Protects Bitdefender files from being altered by unauthorized 3rd party applications.{break}Will generate a high number of alerts.',
                      { break: '<br><br>' }
                    )}"
                  ></gz-text>
                </div>
              </gz-selection-card>
              <gz-selection-card
                searchable-label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Normal].label}"
                label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Normal].translatedLabel}"
                .value=${FirewallIdsProfileType.NORMAL}
                id="ids-profile-normal"
              >
                <div slot="content">
                  <gz-title fontSize="12" fontWeight="600" style="margin-bottom: 8px"
                    >${translateFn('Recommended for most systems')}</gz-title
                  >
                  <gz-text
                    fontSize="12"
                    style="margin-bottom: 8px"
                    .innerHTML="${translateFn(
                      'Blocks DLL injections, installation of malware drivers.{break}Protects Bitdefender files from being altered by unauthorized 3rd party applications.{break}Will generate a moderate number of alerts.',
                      { break: '<br><br>' }
                    )}"
                  >
                  </gz-text>
                </div>
              </gz-selection-card>
              <gz-selection-card
                searchable-label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Permissive].label}"
                label="${this.searchableKeywords[FirewallSettingsSearchableKeywords.Permissive].translatedLabel}"
                .value=${FirewallIdsProfileType.PERMISSIVE}
                id="ids-profile-permissive"
              >
                <div slot="content">
                  <gz-title fontSize="12" fontWeight="600" style="margin-bottom: 8px"
                    >${translateFn('Not recommended')}</gz-title
                  >
                  <gz-text
                    fontSize="12"
                    style="margin-bottom: 8px"
                    .innerHTML="${translateFn(
                      'Protects against installation of malware drivers.{break}Protects Bitdefender files from being altered by unauthorized 3rd party applications.{break}Will generate a low number of alerts.',
                      {
                        break: '<br><br>',
                      }
                    )}"
                  ></gz-text>
                </div>
              </gz-selection-card>
            </gz-selection-card-group>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.firewall.settings
   */
  protected getValueFromManager(): FirewallSettingsFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.FIREWALL]: { [SectionId.SETTINGS]: FIREWALL_SETTINGS_DEFAULT_VALUE },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.FIREWALL]: {
          [SectionId.SETTINGS]: PolicyPanelManager.getInstance().getPolicy('uiSettings.firewall.settings'),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        firewall: {
          settings: {
            enable: this.firewallToggle,
            blockPortScan: this.blockPortScanFormField,
            allowInternetConnectionSharing: this.icsFormField,
            monitorWiFiConnections: this.monitorFormField,
            logVerbosity: {
              enable: this.logCheckboxFormField,
              level: this.logInputFormField,
            },
            instructionsDetectionSystem: {
              enable: this.idsToggleFormField,
              profile: this.idsProfileFormField,
            },
            exclusions: {
              enable: this.exclusionsCheckboxFormField,
              duplicateToNetworkProtection:
                (this.exclusionsItemsFormField.Input.shadowRoot?.querySelector(
                  '#duplicate-field'
                ) as GzFormFieldWebComponent) || new FormGroup({}),
              items: this.exclusionsItemsFormField,
            },
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: this.valueT.uiSettings,
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'firewall', 'settings'], {});
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.firewallInfoText, !event.detail.checked);
    toggleDisabled(this.firewallContent, !event.detail.checked);
    toggleDisabled(this.firewallSettingsInfoText, !event.detail.checked);
    toggleVisibility(this.blocklistRulesWarningText, !event.detail.checked);
  }

  private toggleDisabledBlockPortScan() {
    toggleDisabled(this.exclusionsCheckboxFormField, !this.blockPortScanFormField.value);
    toggleDisabled(
      this.exclusionsContentText,
      !this.exclusionsCheckboxFormField.value || !this.blockPortScanFormField.value
    );
    toggleDisabled(
      this.exclusionsItemsFormField,
      !this.exclusionsCheckboxFormField.value || !this.blockPortScanFormField.value
    );
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();
    this.exclusionsItemsFormField.Input.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_FIREWALL_SETTINGS: GzFirewallSettingsWebComponent;
  }
}
